/**
 * StaffInviteAccept.js
 *
 * Public page for accepting a staff invitation.
 * Route: /admin/invite/:token
 *
 * Flow:
 *  1. On mount: validate token via POST /v1/staff/invites/validate
 *  2. Show invite details (name, role, expiry)
 *  3. Password creation form (min 12 chars, confirmation)
 *  4. Submit: POST /v1/staff/invites/redeem
 *  5. On success: show MFA setup required messaging + redirect to /admin/login
 *
 * Security:
 *  - Raw token only lives in URL params during active flow
 *  - Token not stored in state/localStorage beyond the active page session
 *  - No auth required (user has no credentials yet)
 */

import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';

const API = process.env.REACT_APP_API_URL || 'https://api.visinvoke.com';

const ROLE_LABELS = {
  OWNER:'Owner',CO_OWNER:'Co-Owner',OPERATIONS_ADMIN:'Operations Admin',
  SUPPORT:'Support',MODERATION:'Moderation',CONTENT_MANAGER:'Content Manager',
  GENERATOR_QA:'Generator QA',FINANCE:'Finance',ANALYTICS_VIEWER:'Analytics Viewer',
  COMMUNITY_MANAGER:'Community Manager',DELIVERABILITY_ADMIN:'Deliverability Admin',
  TECH_OPERATOR:'Tech Operator',SECURITY_AUDITOR:'Security Auditor',
  DEVELOPER_LIMITED:'Developer (Limited)',READ_ONLY_ADMIN:'Read-Only Admin',
};

const ROLE_COLORS = {
  OWNER:'#C9A84C',CO_OWNER:'#A855F7',OPERATIONS_ADMIN:'#F97316',
  SUPPORT:'#3B82F6',MODERATION:'#F59E0B',CONTENT_MANAGER:'#22C55E',
  GENERATOR_QA:'#84CC16',FINANCE:'#10B981',ANALYTICS_VIEWER:'#06B6D4',
  COMMUNITY_MANAGER:'#EC4899',DELIVERABILITY_ADMIN:'#14B8A6',TECH_OPERATOR:'#8B5CF6',
  SECURITY_AUDITOR:'#EF4444',DEVELOPER_LIMITED:'#6366F1',READ_ONLY_ADMIN:'#6B7280',
};

const styles = `
  @import url('https://fonts.googleapis.com/css2?family=Syne:wght@600;700;800&family=DM+Sans:wght@400;500;600&display=swap');
  *, *::before, *::after { box-sizing: border-box; margin: 0; padding: 0; }
  body { background: #080B12; color: #E2E8F0; font-family: 'DM Sans', sans-serif; }
  .inv-root { min-height: 100vh; display: flex; align-items: center; justify-content: center; padding: 24px; background: #080B12; }
  .inv-card { background: #111827; border: 1px solid rgba(255,255,255,0.07); border-radius: 20px; width: 100%; max-width: 480px; overflow: hidden; box-shadow: 0 24px 64px rgba(0,0,0,0.6); }
  .inv-header { background: #0F172A; padding: 28px 32px; border-bottom: 1px solid rgba(255,255,255,0.06); }
  .inv-brand { font-family: 'Syne', sans-serif; font-size: 18px; font-weight: 800; color: #C9A84C; letter-spacing: 1px; }
  .inv-brand-sub { font-size: 10px; color: #6B7280; letter-spacing: 3px; text-transform: uppercase; margin-top: 2px; }
  .inv-body { padding: 32px; }
  .inv-title { font-family: 'Syne', sans-serif; font-size: 22px; font-weight: 700; color: #fff; margin-bottom: 6px; }
  .inv-sub { font-size: 13px; color: rgba(255,255,255,0.45); line-height: 1.6; margin-bottom: 24px; }
  .inv-label { display: block; font-size: 11px; font-weight: 600; color: rgba(255,255,255,0.4); letter-spacing: 0.08em; text-transform: uppercase; margin-bottom: 6px; margin-top: 18px; }
  .inv-input { width: 100%; background: rgba(255,255,255,0.05); border: 1px solid rgba(255,255,255,0.1); border-radius: 10px; padding: 12px 16px; font-size: 14px; color: #fff; font-family: 'DM Sans', sans-serif; outline: none; transition: border-color 0.2s; }
  .inv-input:focus { border-color: rgba(201,168,76,0.5); }
  .inv-btn { width: 100%; padding: 14px; background: linear-gradient(135deg,#C9A84C,#A0832E); border: none; border-radius: 10px; font-size: 14px; font-weight: 700; color: #080B12; cursor: pointer; font-family: 'Syne', sans-serif; margin-top: 24px; transition: opacity 0.2s; }
  .inv-btn:disabled { opacity: 0.5; cursor: not-allowed; }
  .inv-err { font-size: 12px; color: #FCA5A5; margin-top: 12px; padding: 10px 14px; background: rgba(239,68,68,0.08); border-radius: 8px; border: 1px solid rgba(239,68,68,0.2); }
  .inv-success { font-size: 12px; color: #4ADE80; margin-top: 12px; padding: 10px 14px; background: rgba(74,222,128,0.08); border-radius: 8px; border: 1px solid rgba(74,222,128,0.2); }
  .inv-info { background: rgba(255,255,255,0.03); border: 1px solid rgba(255,255,255,0.07); border-radius: 12px; padding: 16px; margin-bottom: 20px; }
  .inv-info-row { display: flex; justify-content: space-between; align-items: center; font-size: 12px; padding: 4px 0; }
  .inv-info-label { color: rgba(255,255,255,0.4); }
  .inv-info-val { color: #fff; font-weight: 600; }
  .inv-role-pill { display: inline-block; padding: 3px 10px; border-radius: 6px; font-size: 11px; font-weight: 700; margin: 2px; }
  .inv-hint { font-size: 11px; color: rgba(255,255,255,0.3); margin-top: 6px; }
  .inv-loader { display: flex; align-items: center; justify-content: center; padding: 48px; gap: 10px; color: rgba(255,255,255,0.3); font-size: 13px; }
  .inv-security { font-size: 11px; color: #6B7280; text-align: center; margin-top: 20px; padding-top: 16px; border-top: 1px solid rgba(255,255,255,0.05); line-height: 1.6; }
  .pw-strength { height: 3px; border-radius: 2px; margin-top: 6px; transition: all 0.3s; }
`;

function pwStrength(pw) {
  if (!pw) return { score: 0, color: '#374151', label: '' };
  let score = 0;
  if (pw.length >= 12) score++;
  if (pw.length >= 16) score++;
  if (/[A-Z]/.test(pw)) score++;
  if (/[0-9]/.test(pw)) score++;
  if (/[^A-Za-z0-9]/.test(pw)) score++;
  const map = [
    { color: '#EF4444', label: 'Weak' },
    { color: '#F59E0B', label: 'Fair' },
    { color: '#F59E0B', label: 'Fair' },
    { color: '#22C55E', label: 'Good' },
    { color: '#22C55E', label: 'Strong' },
    { color: '#10B981', label: 'Very strong' },
  ];
  return { score, ...map[Math.min(score, 5)] };
}

export default function StaffInviteAccept() {
  const { token } = useParams();
  const navigate = useNavigate();

  const [phase, setPhase]       = useState('validating'); // validating | form | submitting | success | error
  const [invite, setInvite]     = useState(null);
  const [validateErr, setValidateErr] = useState('');
  const [password, setPassword] = useState('');
  const [confirm, setConfirm]   = useState('');
  const [submitErr, setSubmitErr] = useState('');

  const strength = pwStrength(password);

  const validate = useCallback(async () => {
    if (!token) { setPhase('error'); setValidateErr('No invite token found in URL.'); return; }
    try {
      const r = await fetch(`${API}/v1/staff/invites/validate`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ token }),
      });
      const d = await r.json();
      if (r.ok && d.valid) {
        setInvite(d);
        setPhase('form');
      } else {
        setPhase('error');
        setValidateErr(d.error || 'This invite link is invalid or has expired.');
      }
    } catch {
      setPhase('error');
      setValidateErr('Unable to validate invite. Please check your connection and try again.');
    }
  }, [token]);

  useEffect(() => { validate(); }, [validate]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!password || password.length < 12) { setSubmitErr('Password must be at least 12 characters.'); return; }
    if (password !== confirm) { setSubmitErr('Passwords do not match.'); return; }
    setPhase('submitting');
    setSubmitErr('');
    try {
      const r = await fetch(`${API}/v1/staff/invites/redeem`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ token, password }),
      });
      const d = await r.json();
      if (r.ok) {
        setPhase('success');
      } else {
        setPhase('form');
        setSubmitErr(d.error || 'Redemption failed. Please try again.');
      }
    } catch {
      setPhase('form');
      setSubmitErr('Network error. Please try again.');
    }
  };

  const fmtDate = (iso) => {
    if (!iso) return '-';
    return new Date(iso).toLocaleString('en-AU', { dateStyle: 'medium', timeStyle: 'short' });
  };

  return (
    <>
      <style>{styles}</style>
      <div className="inv-root">
        <div className="inv-card">
          <div className="inv-header">
            <div className="inv-brand">VISINVOKE</div>
            <div className="inv-brand-sub">Staff Access</div>
          </div>
          <div className="inv-body">

            {phase === 'validating' && (
              <div className="inv-loader">
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="#C9A84C" strokeWidth="2">
                  <path d="M12 2v4M12 18v4M4.93 4.93l2.83 2.83M16.24 16.24l2.83 2.83M2 12h4M18 12h4M4.93 19.07l2.83-2.83M16.24 7.76l2.83-2.83"/>
                </svg>
                Validating invite...
              </div>
            )}

            {phase === 'error' && (
              <>
                <div className="inv-title">Invalid Invite</div>
                <div className="inv-sub">{validateErr}</div>
                <div className="inv-err">{validateErr}</div>
                <div className="inv-security">
                  If you believe this is an error, contact your VISINVOKE administrator.
                </div>
              </>
            )}

            {(phase === 'form' || phase === 'submitting') && invite && (
              <>
                <div className="inv-title">Accept Your Invitation</div>
                <div className="inv-sub">
                  You have been invited to the VISINVOKE staff platform. Set a password to activate your account.
                </div>

                <div className="inv-info">
                  <div className="inv-info-row">
                    <span className="inv-info-label">Email</span>
                    <span className="inv-info-val">{invite.email}</span>
                  </div>
                  <div className="inv-info-row" style={{ marginTop: 8 }}>
                    <span className="inv-info-label">Roles</span>
                    <span>
                      {(invite.roles || []).map(r => (
                        <span key={r} className="inv-role-pill" style={{ background: (ROLE_COLORS[r] || '#718096') + '20', color: ROLE_COLORS[r] || '#718096', border: `1px solid ${(ROLE_COLORS[r] || '#718096')}30` }}>
                          {ROLE_LABELS[r] || r}
                        </span>
                      ))}
                    </span>
                  </div>
                  <div className="inv-info-row" style={{ marginTop: 8 }}>
                    <span className="inv-info-label">Expires</span>
                    <span className="inv-info-val" style={{ color: 'rgba(255,255,255,0.5)', fontWeight: 400 }}>{fmtDate(invite.expires_at)}</span>
                  </div>
                </div>

                <form onSubmit={handleSubmit}>
                  <label className="inv-label">Create Password <span style={{ color: '#EF4444' }}>*</span></label>
                  <input
                    className="inv-input"
                    type="password"
                    value={password}
                    onChange={e => setPassword(e.target.value)}
                    placeholder="Min. 12 characters"
                    autoComplete="new-password"
                    required
                  />
                  {password && (
                    <>
                      <div className="pw-strength" style={{ width: `${(strength.score / 5) * 100}%`, background: strength.color }} />
                      <div className="inv-hint" style={{ color: strength.color }}>{strength.label}</div>
                    </>
                  )}

                  <label className="inv-label">Confirm Password <span style={{ color: '#EF4444' }}>*</span></label>
                  <input
                    className="inv-input"
                    type="password"
                    value={confirm}
                    onChange={e => setConfirm(e.target.value)}
                    placeholder="Re-enter password"
                    autoComplete="new-password"
                    required
                  />
                  {confirm && password !== confirm && (
                    <div className="inv-hint" style={{ color: '#EF4444' }}>Passwords do not match</div>
                  )}

                  {submitErr && <div className="inv-err">{submitErr}</div>}

                  <button className="inv-btn" type="submit" disabled={phase === 'submitting' || !password || !confirm}>
                    {phase === 'submitting' ? 'Activating Account...' : 'Activate Account'}
                  </button>
                </form>

                <div className="inv-security">
                  After activation you will be required to set up two-factor authentication before accessing the staff platform. Do not share this link.
                </div>
              </>
            )}

            {phase === 'success' && (
              <>
                <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 16 }}>
                  <div style={{ width: 44, height: 44, borderRadius: 12, background: 'rgba(74,222,128,0.1)', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                    <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="#4ADE80" strokeWidth="2">
                      <path d="M20 6L9 17l-5-5"/>
                    </svg>
                  </div>
                  <div className="inv-title" style={{ marginBottom: 0 }}>Account Activated</div>
                </div>
                <div className="inv-sub">Your password has been set and your account is now active.</div>
                <div className="inv-success" style={{ marginBottom: 16 }}>
                  <strong>MFA setup required.</strong> You must set up two-factor authentication before you can access the staff platform. You will be prompted on your first login.
                </div>
                <button className="inv-btn" onClick={() => navigate('/admin/login')}>
                  Proceed to Login
                </button>
                <div className="inv-security">
                  All staff sessions are monitored and logged. Contact your administrator if you have any questions.
                </div>
              </>
            )}

          </div>
        </div>
      </div>
    </>
  );
}
